import { useEffect, useRef, useState } from "react";
import { buttonVariants } from "@cronika/ui";
import { useContactModal } from "@/core/providers/ContactModalContext";
import { useContent } from "@/shared/lib/i18n";

function useInView(threshold = 0.2) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

export function WhyUs() {
  const { ref, inView } = useInView(0.15);
  const { openModal } = useContactModal();
  const { whyUs } = useContent();
  const [big0, big1, big2] = whyUs.bigCards;
  const [sm0, sm1, sm2] = whyUs.smallCards;

  const titleLines = whyUs.title.split("\n");

  return (
    <section className="py-16 px-[14px] lg:py-24 lg:px-8" id="why-us">
      <div className="mx-auto max-w-[1760px]">
        {/* Header row */}
        <div className="flex flex-col lg:flex-row lg:items-end gap-4 lg:gap-6 mb-8 lg:mb-10">
          <div className="flex-1">
            <h2 className="text-[32px] lg:text-[52px] leading-tight text-white">
              {titleLines[0]}
              <br />
              {titleLines[1]}
            </h2>
          </div>
          <div className="flex-1 lg:max-w-[520px] lg:pt-3">
            <p className="text-[15px] leading-relaxed">{whyUs.subtitle}</p>
          </div>
        </div>

        {/* Card grid */}
        <div ref={ref} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <BigCard
            label={big0!.label}
            description={big0!.description}
            delay={0}
            inView={inView}
          >
            <GaugeRevShare inView={inView} chartLabel={big0!.chartLabel} chartValue={big0!.chartValue} />
          </BigCard>

          <BigCard
            label={big1!.label}
            description={big1!.description}
            delay={120}
            inView={inView}
          >
            <ChartCPA inView={inView} chartLabel={big1!.chartLabel} chartValue={big1!.chartValue} />
          </BigCard>

          <BigCard
            label={big2!.label}
            description={big2!.description}
            delay={240}
            inView={inView}
          >
            <ChartPayouts inView={inView} chartValue={big2!.chartValue} chartLabel={big2!.chartLabel} />
          </BigCard>

          {/* Row 2 */}
          <SmallCard
            icon="/icons/high-convert.svg"
            title={sm0!.title}
            description={sm0!.description}
            delay={360}
            inView={inView}
          />
          <SmallCard
            icon="/icons/tracking.svg"
            title={sm1!.title}
            description={sm1!.description}
            delay={480}
            inView={inView}
          />
          <SmallCard
            icon="/icons/compensation.svg"
            title={sm2!.title}
            description={sm2!.description}
            delay={600}
            inView={inView}
          />
        </div>

        {/* CTA */}
        <div className="mt-8 lg:mt-10 lg:flex lg:justify-center">
          <button
            onClick={openModal}
            className={buttonVariants({
              variant: "glow",
              size: "lg",
              radius: "xl",
              className: "flex w-full lg:w-auto lg:inline-flex",
            })}
          >
            {whyUs.cta}
          </button>
        </div>
      </div>
    </section>
  );
}

function BigCard({
  label,
  sublabel,
  description,
  children,
  delay,
  inView,
}: {
  label: string;
  sublabel?: string;
  description: string;
  children: React.ReactNode;
  delay: number;
  inView: boolean;
}) {
  return (
    <div
      className="rounded-2xl p-6 flex flex-col gap-4 min-h-[347px] transition-all duration-700 ease-out"
      style={{
        background: "rgba(242,242,242,0.078)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
        border: "1px solid rgba(255,255,255,0.1)",
        boxShadow:
          "inset 0px 1px 0px 0px rgba(255,255,255,0.059), 0px 1px 1px 0px rgba(0,0,0,0.122), 0px 1px 1px 0px rgba(0,0,0,0.239)",
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0) scale(1)" : "translateY(32px) scale(0.97)",
        transitionDelay: `${delay}ms`,
      }}
    >
      <div>
        <h2 className="text-[30px] text-white font-medium">{label}</h2>
        {sublabel && <h2 className="text-[30px] text-white">{sublabel}</h2>}
      </div>
      <div className="flex-1">{children}</div>
      <p className="text-sm text-white/50 leading-relaxed">{description}</p>
    </div>
  );
}

function SmallCard({
  icon,
  title,
  description,
  delay,
  inView,
}: {
  icon: string;
  title: string;
  description: string;
  delay: number;
  inView: boolean;
}) {
  return (
    <div
      className="rounded-2xl p-6 flex flex-col min-h-[200px] transition-all duration-700 ease-out"
      style={{
        background: "rgba(242,242,242,0.078)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
        border: "1px solid rgba(255,255,255,0.1)",
        boxShadow:
          "inset 0px 1px 0px 0px rgba(255,255,255,0.059), 0px 1px 1px 0px rgba(0,0,0,0.122), 0px 1px 1px 0px rgba(0,0,0,0.239)",
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(24px)",
        transitionDelay: `${delay}ms`,
      }}
    >
      <p className="text-base font-semibold text-white mb-2">{title}</p>
      <p className="text-sm text-white/50 leading-relaxed">{description}</p>
      <div className="flex-1" />
      <div className="mt-6">
        <img src={icon} width={32} height={32} alt="" />
      </div>
    </div>
  );
}

/* ── Charts ── */

function GaugeRevShare({
  inView,
  chartLabel,
  chartValue,
}: {
  inView: boolean;
  chartLabel: string;
  chartValue: string;
}) {
  const cx = 100,
    cy = 88,
    r = 72;
  const angle = (1 - 0.85) * Math.PI;
  const dotX = cx + r * Math.cos(angle);
  const dotY = cy - r * Math.sin(angle);

  return (
    <svg viewBox="0 0 200 115" className="w-full" style={{ overflow: "visible" }}>
      <defs>
        <linearGradient id="gaugeGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#CA5C00" />
          <stop offset="100%" stopColor="#FFD000" />
        </linearGradient>
        <filter id="gaugeGlow" x="-30%" y="-100%" width="160%" height="400%">
          <feGaussianBlur stdDeviation="7" result="blur" />
        </filter>
      </defs>
      {/* Track */}
      <path
        d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
        fill="none"
        stroke="#27272A"
        strokeWidth="8.8"
        strokeLinecap="round"
        pathLength="100"
      />
      {/* Progress glow */}
      <path
        d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
        fill="none"
        stroke="url(#gaugeGrad)"
        strokeWidth="20"
        strokeLinecap="round"
        pathLength="100"
        strokeDasharray={`${inView ? 85 : 0} 100`}
        filter="url(#gaugeGlow)"
        style={{
          opacity: inView ? 0.55 : 0,
          transition: "stroke-dasharray 1.2s cubic-bezier(0.4,0,0.2,1), opacity 1s ease",
          transitionDelay: "200ms",
        }}
      />
      {/* Progress */}
      <path
        d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
        fill="none"
        stroke="url(#gaugeGrad)"
        strokeWidth="8.8"
        strokeLinecap="round"
        pathLength="100"
        strokeDasharray={`${inView ? 85 : 0} 100`}
        style={{ transition: "stroke-dasharray 1.2s cubic-bezier(0.4,0,0.2,1)", transitionDelay: "200ms" }}
      />
      {/* Glow dot */}
      <circle
        cx={dotX}
        cy={dotY}
        r="12"
        fill="#FFEB90"
        opacity={inView ? 0.2 : 0}
        style={{ transition: "opacity 0.6s ease", transitionDelay: "1s" }}
      />
      <circle
        cx={dotX}
        cy={dotY}
        r="3.5"
        fill="#FFEB90"
        opacity={inView ? 1 : 0}
        style={{ transition: "opacity 0.4s ease", transitionDelay: "1.15s" }}
      />
      {/* Labels */}
      <text
        x={cx}
        y={40}
        textAnchor="middle"
        fill="rgba(255,255,255,0.38)"
        fontSize="7.5"
        fontFamily="'Inter Tight', 'Inter', system-ui, sans-serif"
      >
        {chartLabel}
      </text>
      <text
        x={cx}
        y={72}
        textAnchor="middle"
        fill="white"
        fontSize="36"
        fontWeight="800"
        fontFamily="'Inter Tight', 'Inter', system-ui, sans-serif"
        style={{
          opacity: inView ? 1 : 0,
          transition: "opacity 0.6s ease",
          transitionDelay: "800ms",
        }}
      >
        {chartValue}
      </text>
    </svg>
  );
}

function ChartCPA({
  inView,
  chartLabel,
  chartValue,
}: {
  inView: boolean;
  chartLabel: string;
  chartValue: string;
}) {
  const pathD = "M 0,78 C 40,74 78,42 112,36 C 150,30 188,18 220,8";
  return (
    <svg viewBox="0 0 220 100" className="w-full" style={{ overflow: "visible" }}>
      <defs>
        <linearGradient id="cpaLine" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#CA5C00" />
          <stop offset="100%" stopColor="#FFD000" />
        </linearGradient>
        <linearGradient id="cpaArea" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#c98200" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#3d2000" stopOpacity="0.08" />
        </linearGradient>
        <filter id="cpaGlow">
          <feGaussianBlur stdDeviation="6" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      {/* Area */}
      <path
        d="M 0,78 C 40,74 78,42 112,36 C 150,30 188,18 220,8 L 220,100 L 0,100 Z"
        fill="url(#cpaArea)"
        style={{ opacity: inView ? 1 : 0, transition: "opacity 1s ease", transitionDelay: "400ms" }}
      />
      {/* Line */}
      <path
        d={pathD}
        fill="none"
        stroke="url(#cpaLine)"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeDasharray="400"
        strokeDashoffset={inView ? 0 : 400}
        style={{ transition: "stroke-dashoffset 1.2s cubic-bezier(0.4,0,0.2,1)", transitionDelay: "200ms" }}
      />
      {/* End dot */}
      <circle
        cx="220"
        cy="8"
        r="8"
        fill="#FFEB90"
        opacity={inView ? 0.2 : 0}
        style={{ transition: "opacity 0.4s ease", transitionDelay: "1.25s" }}
      />
      <circle
        cx="220"
        cy="8"
        r="4"
        fill="#FFEB90"
        opacity={inView ? 1 : 0}
        style={{ transition: "opacity 0.4s ease", transitionDelay: "1.3s" }}
      />
      {/* Labels */}
      <text
        x="110"
        y="62"
        textAnchor="middle"
        fill="rgba(255,255,255,0.38)"
        fontSize="7.5"
        fontFamily="'Inter Tight', 'Inter', system-ui, sans-serif"
      >
        {chartLabel}
      </text>
      <text
        x="110"
        y="88"
        textAnchor="middle"
        fill="white"
        fontSize="29"
        fontWeight="800"
        fontFamily="'Inter Tight', 'Inter', system-ui, sans-serif"
        style={{ opacity: inView ? 1 : 0, transition: "opacity 0.6s ease", transitionDelay: "700ms" }}
      >
        {chartValue}
      </text>
    </svg>
  );
}

function ChartPayouts({
  inView,
  chartValue,
  chartLabel,
}: {
  inView: boolean;
  chartValue: string;
  chartLabel: string;
}) {
  const bars = [35, 48, 28, 72, 38, 68, 32, 45, 82, 40, 36];
  const amber = [false, false, false, true, false, true, false, false, true, false, false];
  const barW = 14,
    gap = 6;
  const offsetX = (220 - (bars.length * (barW + gap) - gap)) / 2;
  const maxH = 55,
    bottom = 118;

  return (
    <svg viewBox="0 0 220 125" className="w-full">
      <defs>
        <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fec201" />
          <stop offset="100%" stopColor="#f89a09" />
        </linearGradient>
      </defs>
      <text
        x="110"
        y="46"
        textAnchor="middle"
        fill="white"
        fontSize="29"
        fontWeight="800"
        fontFamily="'Inter Tight', 'Inter', system-ui, sans-serif"
        style={{ opacity: inView ? 1 : 0, transition: "opacity 0.6s ease", transitionDelay: "300ms" }}
      >
        {chartValue}
      </text>
      <text
        x="110"
        y="58"
        textAnchor="middle"
        fill="rgba(255,255,255,0.38)"
        fontSize="7.5"
        fontFamily="'Inter Tight', 'Inter', system-ui, sans-serif"
        style={{ opacity: inView ? 1 : 0, transition: "opacity 0.6s ease", transitionDelay: "400ms" }}
      >
        {chartLabel}
      </text>
      {bars.map((v, i) => {
        const h = (v / 100) * maxH;
        return (
          <rect
            key={i}
            x={offsetX + i * (barW + gap)}
            y={inView ? bottom - h : bottom}
            width={barW}
            height={inView ? h : 0}
            rx="4"
            fill={amber[i] ? "url(#barGrad)" : "#27272A"}
            style={{
              transition: "y 0.6s cubic-bezier(0.4,0,0.2,1), height 0.6s cubic-bezier(0.4,0,0.2,1)",
              transitionDelay: `${300 + i * 60}ms`,
            }}
          />
        );
      })}
    </svg>
  );
}
